import React, { useState, useEffect } from 'react';
import { Input, Spinner, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { personnelAPI, personnelLeaveAPI } from '../../helpers/api';
import { useIsAdmin } from '../../helpers/hooks';

interface SitePersonnelEntry {
  id: string;
  assignedAt: string;
  removedAt: string | null;
  isActive: boolean;
  site: {
    id: string;
    name: string;
    code: string;
    project: {
      id: string;
      name: string;
      code: string;
      city: { name: string };
    };
  };
}

interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  tcNo: string | null;
  phone: string | null;
  title: string | null;
  dailyWage: number | null;
  isActive: boolean;
  status: string;
  statusNote: string | null;
  type: string;
  employmentType: string;
  monthlySalary: number | null;
  rating: number | null;
  comment: string | null;
  startDate: string | null;
}

const personnelStatusLabels: Record<string, string> = {
  ACTIVE: 'Aktif',
  INACTIVE: 'Pasif',
  ON_LEAVE: 'İzinli',
  ON_REPORT: 'Raporlu',
};

const personnelStatusStyles: Record<string, string> = {
  ACTIVE: 'bg-success bg-opacity-10 text-success',
  INACTIVE: 'bg-secondary bg-opacity-10 text-secondary',
  ON_LEAVE: 'bg-warning bg-opacity-10 text-warning',
  ON_REPORT: 'bg-info bg-opacity-10 text-info',
};

interface PersonnelDetailModalProps {
  isOpen: boolean;
  toggle: () => void;
  personnel: Personnel | null;
  onUpdate?: () => void;
}

// Default title options
const DEFAULT_TITLES = [
  'İşçi',
  'Kalfa',
  'Usta',
  'Operatör',
  'Elektrikçi',
  'Tesisatçı',
  'Demir Ustası',
  'Kaynakçı',
];

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const calculateDuration = (startDate: string, endDate: string | null): string => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 30) {
    return `${diffDays} gün`;
  } else if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    return days > 0 ? `${months} ay ${days} gün` : `${months} ay`;
  } else {
    const years = Math.floor(diffDays / 365);
    const remainingDays = diffDays % 365;
    const months = Math.floor(remainingDays / 30);
    return months > 0 ? `${years} yıl ${months} ay` : `${years} yıl`;
  }
};

// Telefon numarasını TR formatına çevir: 533 123 12 23
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  // Başındaki 0 veya 90'ı kaldır
  let cleaned = digits;
  if (cleaned.startsWith('90') && cleaned.length > 10) cleaned = cleaned.slice(2);
  if (cleaned.startsWith('0') && cleaned.length > 10) cleaned = cleaned.slice(1);
  cleaned = cleaned.slice(0, 10);

  // Formatla: XXX XXX XX XX
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  if (cleaned.length <= 8) return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`;
};

// Formatlanmış telefondan sadece rakamları al (API'ye gönderirken)
const unformatPhone = (value: string): string => value.replace(/\D/g, '');

const PersonnelDetailModal: React.FC<PersonnelDetailModalProps> = ({
  isOpen,
  toggle,
  personnel: personnelProp,
  onUpdate,
}) => {
  const isAdmin = useIsAdmin();
  const [localPersonnel, setLocalPersonnel] = useState<Personnel | null>(null);
  const personnel = localPersonnel || personnelProp;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('ACTIVE');
  const [currentStatusNote, setCurrentStatusNote] = useState('');
  const [showStatusChange, setShowStatusChange] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newStatusNote, setNewStatusNote] = useState('');
  const [showRatingEdit, setShowRatingEdit] = useState(false);
  const [editRating, setEditRating] = useState('');
  const [editComment, setEditComment] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    tcNo: '',
    phone: '',
    title: '',
    customTitle: '',
    dailyWage: '',
    startDate: '',
    type: 'COMPANY',
    employmentType: 'DAILY',
    monthlySalary: '',
  });

  // Work history state
  const [workHistory, setWorkHistory] = useState<SitePersonnelEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Activity timeline state
  const [activity, setActivity] = useState<Array<{ date: string; type: 'work' | 'leave'; detail: any }>>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Leave section state
  const [leaves, setLeaves] = useState<Array<{ id: string; date: string; type: string; notes: string | null }>>([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [showLeaveForm, setShowLeaveForm] = useState(false);
  const [savingLeave, setSavingLeave] = useState(false);
  const [leaveBulk, setLeaveBulk] = useState(false);
  const [leaveData, setLeaveData] = useState({ date: '', startDate: '', endDate: '', type: 'IZIN', notes: '' });

  // Titles for dropdown
  const [titles, setTitles] = useState<string[]>([]);

  // Reset form and fetch history when personnel changes
  useEffect(() => {
    if (personnelProp && isOpen) {
      setLocalPersonnel(null); // prop'tan gelen veriyi kullan
      setFormData({
        firstName: personnelProp.firstName || '',
        lastName: personnelProp.lastName || '',
        tcNo: personnelProp.tcNo || '',
        phone: formatPhone(personnelProp.phone || ''),
        title: personnelProp.title || '',
        customTitle: '',
        dailyWage: personnelProp.dailyWage?.toString() || '',
        startDate: personnelProp.startDate ? personnelProp.startDate.split('T')[0] : '',
        type: personnelProp.type || 'COMPANY',
        employmentType: personnelProp.employmentType || 'DAILY',
        monthlySalary: personnelProp.monthlySalary?.toString() || '',
      });
      setCurrentStatus(personnelProp.status || (personnelProp.isActive ? 'ACTIVE' : 'INACTIVE'));
      setCurrentStatusNote(personnelProp.statusNote || '');
      setShowStatusChange(false);
      setShowRatingEdit(false);
      setShowLeaveForm(false);
      setIsEditing(false);
      fetchWorkHistory(personnelProp.id);
      fetchActivity(personnelProp.id);
      fetchLeaves(personnelProp.id);
    }
  }, [personnelProp, isOpen]);

  // Update form data when titles are loaded (to handle custom values)
  useEffect(() => {
    if (personnel && titles.length > 0) {
      const titleInList = titles.includes(personnel.title || '');
      setFormData(prev => ({
        ...prev,
        title: titleInList ? (personnel.title || '') : (personnel.title ? '__OTHER__' : ''),
        customTitle: titleInList ? '' : (personnel.title || ''),
      }));
    }
  }, [personnel, titles]);

  // Kayıt sonrası personeli yeniden çek
  const refreshPersonnel = async () => {
    if (!personnel) return;
    try {
      const response = await personnelAPI.getOne(personnel.id);
      const data = response.data;
      setLocalPersonnel(data);
      setCurrentStatus(data.status || 'ACTIVE');
      setCurrentStatusNote(data.statusNote || '');
    } catch {
      // sessiz hata
    }
  };

  const fetchWorkHistory = async (personnelId: string) => {
    try {
      setLoadingHistory(true);
      const response = await personnelAPI.getWorkHistory(personnelId);
      setWorkHistory(response.data);
    } catch (error) {
      console.error('Work history fetch error:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchActivity = async (personnelId: string) => {
    try {
      setLoadingActivity(true);
      const response = await personnelAPI.getActivity(personnelId, 30);
      setActivity(response.data);
    } catch (error) {
      console.error('Activity fetch error:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchLeaves = async (personnelId: string) => {
    try {
      setLoadingLeaves(true);
      const res = await personnelLeaveAPI.getAll({ personnelId });
      setLeaves(res.data);
    } catch (error) {
      console.error('Leaves fetch error:', error);
    } finally {
      setLoadingLeaves(false);
    }
  };

  const fetchTitles = async () => {
    try {
      const response = await personnelAPI.getTitles();
      const dbTitles = response.data as string[];
      const allTitles = [...new Set([...dbTitles, ...DEFAULT_TITLES])].sort();
      setTitles(allTitles);
    } catch (error) {
      console.error('Titles fetch error:', error);
      setTitles(DEFAULT_TITLES);
    }
  };

  // Get actual title value
  const getActualTitle = () => formData.title === '__OTHER__' ? formData.customTitle : formData.title;

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'tcNo') {
      setFormData((prev) => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 11) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSave = async () => {
    if (!personnel) return;

    try {
      setSaving(true);
      const actualTitle = getActualTitle();

      await personnelAPI.update(personnel.id, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        tcNo: formData.tcNo || undefined,
        phone: formData.phone ? unformatPhone(formData.phone) : undefined,
        title: actualTitle || undefined,
        startDate: formData.startDate || undefined,
        type: formData.type as any,
        employmentType: formData.employmentType as any,
        dailyWage: formData.employmentType === 'DAILY' && formData.dailyWage ? parseFloat(formData.dailyWage) : undefined,
        monthlySalary: formData.employmentType === 'MONTHLY' && formData.monthlySalary ? parseFloat(formData.monthlySalary) : undefined,
      });
      toast.success('Personel güncellendi');
      setIsEditing(false);
      onUpdate?.();
      refreshPersonnel();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Güncelleme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (personnel && titles.length > 0) {
      const titleInList = titles.includes(personnel.title || '');
      setFormData({
        firstName: personnel.firstName || '',
        lastName: personnel.lastName || '',
        tcNo: personnel.tcNo || '',
        phone: formatPhone(personnel.phone || ''),
        title: titleInList ? (personnel.title || '') : (personnel.title ? '__OTHER__' : ''),
        customTitle: titleInList ? '' : (personnel.title || ''),
        dailyWage: personnel.dailyWage?.toString() || '',
        startDate: personnel.startDate ? personnel.startDate.split('T')[0] : '',
        type: personnel.type || 'COMPANY',
        employmentType: personnel.employmentType || 'DAILY',
        monthlySalary: personnel.monthlySalary?.toString() || '',
      });
    }
    setIsEditing(false);
  };

  const handleStatusChange = async () => {
    if (!personnel || !newStatus) return;

    try {
      await personnelAPI.update(personnel.id, {
        status: newStatus,
        statusNote: newStatusNote || undefined,
      });
      setCurrentStatus(newStatus);
      setCurrentStatusNote(newStatusNote);
      setShowStatusChange(false);
      setNewStatus('');
      setNewStatusNote('');
      toast.success(`Personel durumu "${personnelStatusLabels[newStatus]}" olarak güncellendi`);
      onUpdate?.();
      refreshPersonnel();
    } catch (error: any) {
      toast.error('İşlem başarısız');
    }
  };

  const handleRatingSave = async () => {
    if (!personnel) return;
    try {
      setSavingRating(true);
      await personnelAPI.update(personnel.id, {
        rating: editRating ? parseInt(editRating) : null,
        comment: editComment || null,
      });
      toast.success('Puan ve yorum güncellendi');
      setShowRatingEdit(false);
      onUpdate?.();
      refreshPersonnel();
    } catch (error: any) {
      toast.error('Güncelleme başarısız');
    } finally {
      setSavingRating(false);
    }
  };

  const handleLeaveSave = async () => {
    if (!personnel) return;
    try {
      setSavingLeave(true);
      if (leaveBulk) {
        if (!leaveData.startDate || !leaveData.endDate) {
          toast.error('Tarih aralığı seçilmeli');
          return;
        }
        const result = await personnelLeaveAPI.createBulk({
          personnelId: personnel.id,
          startDate: leaveData.startDate,
          endDate: leaveData.endDate,
          type: leaveData.type,
          notes: leaveData.notes || undefined,
        });
        toast.success(`${result.data.created} gün izin kaydı oluşturuldu`);
      } else {
        if (!leaveData.date) {
          toast.error('Tarih seçilmeli');
          return;
        }
        await personnelLeaveAPI.create({
          personnelId: personnel.id,
          date: leaveData.date,
          type: leaveData.type,
          notes: leaveData.notes || undefined,
        });
        toast.success('İzin kaydı oluşturuldu');
      }
      setShowLeaveForm(false);
      setLeaveData({ date: '', startDate: '', endDate: '', type: 'IZIN', notes: '' });
      fetchActivity(personnel.id);
      fetchLeaves(personnel.id);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Kayıt oluşturulamadı');
    } finally {
      setSavingLeave(false);
    }
  };

  const handleLeaveDelete = async (leaveId: string) => {
    if (!personnel) return;
    try {
      await personnelLeaveAPI.delete(leaveId);
      toast.success('İzin kaydı silindi');
      fetchLeaves(personnel.id);
      fetchActivity(personnel.id);
    } catch {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleDeletePersonnel = async () => {
    if (!personnel) return;
    if (!window.confirm('Bu personeli silmek istediğinizden emin misiniz?')) return;
    try {
      await personnelAPI.delete(personnel.id);
      toast.success('Personel silindi');
      toggle();
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Silme başarısız');
    }
  };

  if (!personnel) return null;

  // Active and past sites
  const activeSites = workHistory.filter((sp) => sp.isActive);
  const pastSites = workHistory.filter((sp) => !sp.isActive);

  const headerActions = (
    <>
      {isAdmin && (
        <>
          {!isEditing ? (
            <>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={() => {
                  fetchTitles();
                  setIsEditing(true);
                }}
                title="Düzenle"
              >
                <i className="ri-edit-line"></i>
              </button>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleDeletePersonnel}
                title="Sil"
                style={{ color: '#f06548' }}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleSave}
                disabled={saving}
                title="Kaydet"
                style={{ color: saving ? '#adb5bd' : '#0ab39c' }}
              >
                {saving ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
              </button>
              <button
                type="button"
                className="side-modal-expand-btn me-1"
                onClick={handleCancel}
                disabled={saving}
                title="İptal"
              >
                <i className="ri-close-line"></i>
              </button>
            </>
          )}
        </>
      )}
      <button
        type="button"
        className="side-modal-expand-btn"
        onClick={toggleExpand}
        title={isExpanded ? 'Küçült' : 'Genişlet'}
      >
        <i className={isExpanded ? 'ri-contract-left-right-line' : 'ri-expand-left-right-line'}></i>
      </button>
    </>
  );

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Personel Detayı"
      size={isExpanded ? 'expanded' : 'lg'}
      headerActions={headerActions}
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        {/* Header Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-start justify-content-between">
            <div className="d-flex align-items-center" style={{ flex: 1 }}>
              <div
                className="avatar-lg rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                style={{ width: 56, height: 56, fontSize: '1.25rem', flexShrink: 0 }}
              >
                {personnel.firstName[0]}{personnel.lastName[0]}
              </div>
              <div style={{ flex: 1 }}>
                {isEditing ? (
                  <div className="d-flex gap-2 mb-2">
                    <Input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Ad"
                      bsSize="sm"
                      style={{ width: '100px' }}
                    />
                    <Input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Soyad"
                      bsSize="sm"
                      style={{ width: '100px' }}
                    />
                  </div>
                ) : (
                  <h4 className="mb-1" style={{ fontWeight: 600 }}>
                    {personnel.firstName} {personnel.lastName}
                  </h4>
                )}
                {isEditing ? (
                  <div className="d-flex flex-column gap-2">
                    <Input
                      type="select"
                      value={formData.title}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData({
                          ...formData,
                          title: value,
                          customTitle: value === '__OTHER__' ? formData.customTitle : '',
                        });
                      }}
                      bsSize="sm"
                      style={{ width: '150px' }}
                    >
                      <option value="">Ünvan Seçin</option>
                      {titles.map((title) => (
                        <option key={title} value={title}>{title}</option>
                      ))}
                      <option value="__OTHER__">Diğer</option>
                    </Input>
                    {formData.title === '__OTHER__' && (
                      <Input
                        type="text"
                        value={formData.customTitle}
                        onChange={(e) => setFormData({ ...formData, customTitle: e.target.value })}
                        placeholder="Yeni ünvan"
                        bsSize="sm"
                        style={{ width: '150px' }}
                      />
                    )}
                  </div>
                ) : (
                  <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
                    {personnel.title || 'İşçi'}
                  </p>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <span
                className={`px-2 py-1 rounded-pill small ${personnelStatusStyles[currentStatus] || personnelStatusStyles.ACTIVE}`}
                style={{ fontWeight: 500 }}
              >
                {personnelStatusLabels[currentStatus] || 'Aktif'}
              </span>
              {isAdmin && !isEditing && (
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 text-muted"
                  onClick={() => {
                    setNewStatus(currentStatus);
                    setNewStatusNote(currentStatusNote);
                    setShowStatusChange(!showStatusChange);
                  }}
                  title="Durumu Değiştir"
                >
                  <i className="ri-refresh-line"></i>
                </button>
              )}
            </div>
          </div>

          {/* Status Note */}
          {currentStatusNote && currentStatus !== 'ACTIVE' && (
            <div className="mt-2 p-2 rounded small text-muted" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-sticky-note-line me-1"></i>
              {currentStatusNote}
            </div>
          )}
        </div>
        {/* End Header Card */}

        {/* Status Change Panel */}
        {showStatusChange && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>Durum Değiştir</span>
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-muted"
                onClick={() => setShowStatusChange(false)}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <Input
              type="select"
              bsSize="sm"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="mb-2"
            >
              <option value="ACTIVE">Aktif</option>
              <option value="INACTIVE">Pasif</option>
              <option value="ON_LEAVE">İzinli</option>
              <option value="ON_REPORT">Raporlu</option>
            </Input>
            <Input
              type="text"
              bsSize="sm"
              value={newStatusNote}
              onChange={(e) => setNewStatusNote(e.target.value)}
              placeholder="Durum notu (opsiyonel)"
              className="mb-2"
            />
            <button
              type="button"
              className="btn btn-sm btn-primary w-100"
              onClick={handleStatusChange}
              disabled={newStatus === currentStatus && newStatusNote === currentStatusNote}
            >
              Güncelle
            </button>
          </div>
        )}

        {/* Details Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
          <div className="text-muted small mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
            Detaylar
          </div>
          <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
            {/* TC No */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">TC No</span>
              {isEditing ? (
                <Input
                  type="text"
                  name="tcNo"
                  value={formData.tcNo}
                  onChange={handleInputChange}
                  placeholder="TC No"
                  bsSize="sm"
                  style={{ width: '130px' }}
                  maxLength={11}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>{personnel.tcNo || '-'}</span>
              )}
            </div>
            {/* Telefon */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Telefon</span>
              {isEditing ? (
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: formatPhone(e.target.value) }))}
                  placeholder="5XX XXX XX XX"
                  bsSize="sm"
                  style={{ width: '140px' }}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {personnel.phone ? (
                    <a href={`tel:${personnel.phone}`}>{formatPhone(personnel.phone)}</a>
                  ) : '-'}
                </span>
              )}
            </div>
            {/* Çalışma Tipi */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Çalışma Tipi</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="employmentType"
                  value={formData.employmentType}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '120px' }}
                >
                  <option value="DAILY">Yevmiyeci</option>
                  <option value="MONTHLY">Aylıkçı</option>
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {personnel.employmentType === 'MONTHLY' ? 'Aylıkçı' : 'Yevmiyeci'}
                </span>
              )}
            </div>
            {/* Ücret */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">
                {(isEditing ? formData.employmentType : personnel.employmentType) === 'MONTHLY' ? 'Aylık Maaş' : 'Günlük Ücret'}
              </span>
              {isEditing ? (
                formData.employmentType === 'MONTHLY' ? (
                  <Input
                    type="number"
                    name="monthlySalary"
                    value={formData.monthlySalary}
                    onChange={handleInputChange}
                    placeholder="TL"
                    bsSize="sm"
                    style={{ width: '120px' }}
                    min={0}
                  />
                ) : (
                  <Input
                    type="number"
                    name="dailyWage"
                    value={formData.dailyWage}
                    onChange={handleInputChange}
                    placeholder="TL"
                    bsSize="sm"
                    style={{ width: '100px' }}
                    min={0}
                  />
                )
              ) : (
                <span style={{ fontWeight: 500, color: (personnel.dailyWage || personnel.monthlySalary) ? '#0ab39c' : undefined }}>
                  {personnel.employmentType === 'MONTHLY'
                    ? (personnel.monthlySalary ? `${personnel.monthlySalary.toLocaleString('tr-TR')} TL` : '-')
                    : (personnel.dailyWage ? `${personnel.dailyWage.toLocaleString('tr-TR')} TL` : '-')}
                </span>
              )}
            </div>
            {/* İşe Başlama */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">İşe Başlama</span>
              {isEditing ? (
                <Input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '140px' }}
                />
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {personnel.startDate ? formatDate(personnel.startDate) : '-'}
                </span>
              )}
            </div>
            {/* Personel Türü */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Tür</span>
              {isEditing ? (
                <Input
                  type="select"
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  bsSize="sm"
                  style={{ width: '130px' }}
                >
                  <option value="COMPANY">Şirket</option>
                  <option value="SUBCONTRACTOR">Taşeron</option>
                </Input>
              ) : (
                <span style={{ fontWeight: 500 }}>
                  {personnel.type === 'SUBCONTRACTOR' ? 'Taşeron' : 'Şirket'}
                </span>
              )}
            </div>
            {/* Puan */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ borderBottom: '1px solid #e9ecef' }}>
              <span className="text-muted">Puan</span>
              <div className="d-flex align-items-center gap-2">
                <span style={{ fontWeight: 500, color: personnel.rating ? '#f7b84b' : undefined }}>
                  {personnel.rating ? '★'.repeat(personnel.rating) + '☆'.repeat(5 - personnel.rating) : '-'}
                </span>
                {isAdmin && !isEditing && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-muted"
                    onClick={() => {
                      setEditRating(personnel.rating?.toString() || '');
                      setEditComment(personnel.comment || '');
                      setShowRatingEdit(!showRatingEdit);
                    }}
                    title="Puan/Yorum Düzenle"
                  >
                    <i className="ri-edit-line" style={{ fontSize: '0.8rem' }}></i>
                  </button>
                )}
              </div>
            </div>
            {/* Yorum */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2">
              <span className="text-muted">Yorum</span>
              <span className="text-muted small" style={{ maxWidth: '60%', textAlign: 'right' }}>
                {personnel.comment || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Rating/Comment Edit Panel */}
        {showRatingEdit && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>Puan & Yorum</span>
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-muted"
                onClick={() => setShowRatingEdit(false)}
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="mb-2">
              <label className="form-label small text-muted mb-1">Puan</label>
              <div className="d-flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className="btn btn-sm p-0"
                    onClick={() => setEditRating(editRating === star.toString() ? '' : star.toString())}
                    style={{
                      fontSize: '1.5rem',
                      color: star <= (parseInt(editRating) || 0) ? '#f7b84b' : '#dee2e6',
                      lineHeight: 1,
                    }}
                  >
                    ★
                  </button>
                ))}
                {editRating && (
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-muted ms-2"
                    onClick={() => setEditRating('')}
                    title="Puanı Kaldır"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label small text-muted mb-1">Yorum</label>
              <Input
                type="textarea"
                value={editComment}
                onChange={(e) => setEditComment(e.target.value)}
                placeholder="Personel hakkında not..."
                bsSize="sm"
                rows={2}
              />
            </div>
            <button
              type="button"
              className="btn btn-sm btn-primary w-100"
              onClick={handleRatingSave}
              disabled={savingRating}
            >
              {savingRating ? <Spinner size="sm" /> : 'Kaydet'}
            </button>
          </div>
        )}

        {/* Active Sites Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #0ab39c' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-building-line me-1 text-success"></i>
              Aktif Şantiyeler
            </span>
            <Badge color="success">{activeSites.length}</Badge>
          </div>

          {loadingHistory ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
              <p className="mb-0 mt-2 text-muted small">Yükleniyor...</p>
            </div>
          ) : activeSites.length === 0 ? (
            <div className="text-center py-4 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <i className="ri-building-line text-muted" style={{ fontSize: '2rem' }}></i>
              <p className="mb-0 mt-2 text-muted">Atanmış şantiye yok</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              {activeSites.map((sp, idx) => (
                <div
                  key={sp.id}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < activeSites.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <strong>{sp.site.name}</strong>
                      <small className="text-muted d-block">
                        {sp.site.project.city.name} / {sp.site.project.name}
                      </small>
                    </div>
                    <Badge color="success">{calculateDuration(sp.assignedAt, null)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Sites Card */}
        {pastSites.length > 0 && (
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #878a99' }} className="rounded-3 p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-history-line me-1"></i>
                Çalışma Geçmişi
              </span>
              <Badge color="secondary">{pastSites.length}</Badge>
            </div>

            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7', maxHeight: '200px', overflowY: 'auto' }}>
              {pastSites.map((sp, idx) => (
                <div
                  key={sp.id}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < pastSites.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <strong>{sp.site.name}</strong>
                      <small className="text-muted d-block">
                        {sp.site.project.city.name} / {sp.site.project.name}
                      </small>
                      <small className="text-muted">
                        {formatDate(sp.assignedAt)} - {sp.removedAt ? formatDate(sp.removedAt) : '-'}
                      </small>
                    </div>
                    <Badge color="secondary">{calculateDuration(sp.assignedAt, sp.removedAt)}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* İzinler Card */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f7b84b' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-calendar-close-line me-1" style={{ color: '#f7b84b' }}></i>
              İzinler
            </span>
            <div className="d-flex align-items-center gap-2">
              <Badge color="warning">{leaves.length}</Badge>
              {isAdmin && !showLeaveForm && (
                <button
                  type="button"
                  className="btn btn-sm btn-soft-warning px-2 py-0"
                  onClick={() => {
                    setShowLeaveForm(true);
                    setLeaveBulk(false);
                    setLeaveData({ date: '', startDate: '', endDate: '', type: 'IZIN', notes: '' });
                  }}
                  style={{ fontSize: '0.7rem' }}
                >
                  <i className="ri-add-line me-1"></i>Ekle
                </button>
              )}
            </div>
          </div>

          {/* Leave Add Form */}
          {showLeaveForm && (
            <div className="rounded-3 p-3 mb-3" style={{ background: '#f8f9fb', border: '1px solid #e9ecef' }}>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="small" style={{ fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Yeni İzin Kaydı</span>
                <div className="d-flex align-items-center gap-2">
                  <div className="form-check form-switch form-switch-sm mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={leaveBulk}
                      onChange={(e) => setLeaveBulk(e.target.checked)}
                      id="leaveBulkSwitch"
                    />
                    <label className="form-check-label small text-muted" htmlFor="leaveBulkSwitch">Toplu</label>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-link p-0 text-muted"
                    onClick={() => setShowLeaveForm(false)}
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
              {leaveBulk ? (
                <div className="d-flex gap-2 mb-2">
                  <Input type="date" bsSize="sm" value={leaveData.startDate} onChange={(e) => setLeaveData({ ...leaveData, startDate: e.target.value })} />
                  <Input type="date" bsSize="sm" value={leaveData.endDate} onChange={(e) => setLeaveData({ ...leaveData, endDate: e.target.value })} />
                </div>
              ) : (
                <div className="mb-2">
                  <Input type="date" bsSize="sm" value={leaveData.date} onChange={(e) => setLeaveData({ ...leaveData, date: e.target.value })} />
                </div>
              )}
              <div className="d-flex gap-2 mb-2">
                <Input
                  type="select"
                  bsSize="sm"
                  value={leaveData.type}
                  onChange={(e) => setLeaveData({ ...leaveData, type: e.target.value })}
                >
                  <option value="IZIN">İzin</option>
                  <option value="RAPOR">Rapor</option>
                  <option value="DEVAMSIZLIK">Devamsızlık</option>
                </Input>
                <Input
                  type="text"
                  bsSize="sm"
                  value={leaveData.notes}
                  onChange={(e) => setLeaveData({ ...leaveData, notes: e.target.value })}
                  placeholder="Not (opsiyonel)"
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-primary w-100"
                onClick={handleLeaveSave}
                disabled={savingLeave}
              >
                {savingLeave ? <Spinner size="sm" /> : 'Kaydet'}
              </button>
            </div>
          )}

          {/* Leave List */}
          {loadingLeaves ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
            </div>
          ) : leaves.length === 0 && !showLeaveForm ? (
            <div className="text-center py-3 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <p className="mb-0 text-muted small">Kayıtlı izin yok</p>
            </div>
          ) : leaves.length > 0 ? (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7', maxHeight: '250px', overflowY: 'auto' }}>
              {leaves.map((leave, idx) => (
                <div
                  key={leave.id}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < leaves.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <Badge
                        color={leave.type === 'IZIN' ? 'warning' : leave.type === 'RAPOR' ? 'info' : 'danger'}
                        className="me-2"
                        style={{ fontSize: '0.6rem' }}
                      >
                        {leave.type === 'IZIN' ? 'İzin' : leave.type === 'RAPOR' ? 'Rapor' : 'Devamsızlık'}
                      </Badge>
                      <span className="small">
                        {new Date(leave.date).toLocaleDateString('tr-TR', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      {leave.notes && <span className="text-muted small ms-2">{leave.notes}</span>}
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-danger"
                        onClick={() => handleLeaveDelete(leave.id)}
                        title="Sil"
                        style={{ fontSize: '0.75rem' }}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        {/* Activity Timeline */}
        <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #405189' }} className="rounded-3 p-3 mb-3">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="text-muted small" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-time-line me-1"></i>
              Son Aktiviteler
            </span>
            <Badge color="primary">{activity.length}</Badge>
          </div>

          {loadingActivity ? (
            <div className="text-center py-4">
              <Spinner size="sm" color="primary" />
            </div>
          ) : activity.length === 0 ? (
            <div className="text-center py-4 rounded-3" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7' }}>
              <p className="mb-0 text-muted">Henüz aktivite yok</p>
            </div>
          ) : (
            <div className="rounded-3 overflow-hidden" style={{ background: '#f8f9fb', border: '1px solid #f2f4f7', maxHeight: '300px', overflowY: 'auto' }}>
              {activity.map((item, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2"
                  style={{ borderBottom: idx < activity.length - 1 ? '1px solid #e9ecef' : 'none' }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      {item.type === 'work' ? (
                        <span className="me-2" style={{ color: '#0ab39c' }}>
                          <i className="ri-hammer-line"></i>
                        </span>
                      ) : (
                        <span className="me-2" style={{ color: item.detail.leaveType === 'DEVAMSIZLIK' ? '#f06548' : '#f7b84b' }}>
                          <i className="ri-calendar-close-line"></i>
                        </span>
                      )}
                      <div>
                        {item.type === 'work' ? (
                          <span className="small">
                            {item.detail.projectName} / {item.detail.siteName}
                            <span className="text-muted ms-1">
                              ({item.detail.wageMultiplier === 1 ? 'Tam' : item.detail.wageMultiplier === 0.5 ? 'Yarım' : item.detail.wageMultiplier})
                            </span>
                          </span>
                        ) : (
                          <span className="small">
                            <Badge
                              color={item.detail.leaveType === 'IZIN' ? 'warning' : item.detail.leaveType === 'RAPOR' ? 'info' : 'danger'}
                              className="me-1"
                              style={{ fontSize: '0.65rem' }}
                            >
                              {item.detail.leaveType === 'IZIN' ? 'İzin' : item.detail.leaveType === 'RAPOR' ? 'Rapor' : 'Devamsızlık'}
                            </Badge>
                            {item.detail.notes && <span className="text-muted">{item.detail.notes}</span>}
                          </span>
                        )}
                      </div>
                    </div>
                    <small className="text-muted flex-shrink-0 ms-2">
                      {new Date(item.date).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                    </small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SideModal>
  );
};

export default PersonnelDetailModal;
